import React, { useEffect, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { ChevronDown, ChevronRight, FileCheck2 } from 'lucide-react';
import { toast } from 'sonner';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import StatusBadge from '../../Common/StatusBadge';
import { apiError, fetchDebtorAccount, fetchProfiles, updateDebtorAccount } from './paymentsApi';

/**
 * "Betalfiler" settings for one company bank account (bank_account_mappings
 * row): bank profile, IBAN/BIC and the bank's agreement ids. Rendered under
 * each mapped row in Inställningar → Bankkonton. Saving recomputes
 * `payments_enabled` on the server.
 */
const PaymentFileSettings = ({ companyId, mapping }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [account, setAccount] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [form, setForm] = useState({ iban: '', bic: '', bank_profile: '', payment_agreement_id: '', payment_signer_id: '' });
  const [errors, setErrors] = useState({});

  const enabled = account ? account.payments_enabled : Boolean(mapping.payments_enabled);

  useEffect(() => {
    if (!open || account) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([fetchProfiles(companyId), fetchDebtorAccount(companyId, mapping.mapping_id)])
      .then(([p, d]) => {
        if (cancelled) return;
        // Clear the spinner before storing the account: setAccount re-runs
        // this effect, whose cleanup marks this run as cancelled.
        setLoading(false);
        setProfiles(p);
        setPrefill(d.prefill);
        setForm({
          iban: d.account.iban || '',
          bic: d.account.bic || '',
          bank_profile: d.account.bank_profile || '',
          payment_agreement_id: d.account.payment_agreement_id || '',
          payment_signer_id: d.account.payment_signer_id || '',
        });
        setAccount(d.account);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoading(false);
        setOpen(false);
        toast.error(apiError(err, 'Kunde inte hämta betalfilsinställningar.').message);
      });
    return () => { cancelled = true; };
  }, [open, account, companyId, mapping.mapping_id]);

  const profile = profiles.find((p) => p.key === form.bank_profile) || null;
  const options = profiles
    .filter((p) => p.key !== 'generic_v03')
    .map((p) => ({ value: p.key, label: p.selectable ? p.name : `${p.name} (inte tillgänglig ännu)`, isDisabled: !p.selectable }));

  const set = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const save = async () => {
    setSaving(true);
    setErrors({});
    try {
      const saved = await updateDebtorAccount(companyId, mapping.mapping_id, {
        iban: form.iban || null,
        bic: form.bic || null,
        bank_profile: form.bank_profile || null,
        payment_agreement_id: form.payment_agreement_id || null,
        payment_signer_id: form.payment_signer_id || null,
      });
      setAccount(saved);
      toast.success(saved.payments_enabled ? 'Betalfiler är aktiverade för kontot.' : 'Inställningarna är sparade.');
    } catch (err) {
      const e = apiError(err, 'Kunde inte spara.');
      if (Array.isArray(e.details)) setErrors(Object.fromEntries(e.details.map((d) => [d.field, d.message])));
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
      <button
        type="button"
        className="ll-btn ll-btn--ghost ll-btn--sm"
        onClick={() => setOpen((o) => !o)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        aria-expanded={open}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <FileCheck2 size={14} />
        Betalfiler
        <StatusBadge tone={enabled ? 'success' : 'muted'} className="ms-1">
          {enabled ? 'Aktiverade' : 'Inte aktiverade'}
        </StatusBadge>
      </button>

      {open && (
        <div style={{ marginTop: 10 }}>
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <div style={{ display: 'grid', gap: 10, maxWidth: 560 }}>
              <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>
                Används när löner och leverantörsbetalningar skapas som betalfil (ISO 20022) för uppladdning i internetbanken.
              </p>

              <Form.Group>
                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Bank för betalfiler</Form.Label>
                <Select
                  options={options}
                  value={options.find((o) => o.value === form.bank_profile) || null}
                  onChange={(o) => set('bank_profile', o ? o.value : '')}
                  isClearable
                  placeholder="Välj bank…"
                  theme={brandSelectTheme}
                  styles={{ menu: (b) => ({ ...b, zIndex: 20 }) }}
                />
                {errors.bank_profile && <div className="text-danger" style={{ fontSize: 12 }}>{errors.bank_profile}</div>}
              </Form.Group>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Form.Group>
                  <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>IBAN</Form.Label>
                  <Form.Control
                    value={form.iban}
                    onChange={(e) => set('iban', e.target.value)}
                    placeholder="SE00 0000 0000 0000 0000 0000"
                    isInvalid={Boolean(errors.iban)}
                  />
                  {errors.iban && <div className="text-danger" style={{ fontSize: 12 }}>{errors.iban}</div>}
                  {prefill?.iban && !form.iban && (
                    <button type="button" className="ll-btn ll-btn--ghost ll-btn--sm" style={{ marginTop: 4 }} onClick={() => set('iban', prefill.iban)}>
                      Hämta från bankkopplingen ({prefill.iban})
                    </button>
                  )}
                  <div className="text-muted" style={{ fontSize: 12 }}>Utan IBAN används clearing- och kontonummer.</div>
                </Form.Group>
                <Form.Group>
                  <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>BIC (valfritt)</Form.Label>
                  <Form.Control value={form.bic} onChange={(e) => set('bic', e.target.value)} placeholder="SWEDSESS" isInvalid={Boolean(errors.bic)} />
                  {errors.bic && <div className="text-danger" style={{ fontSize: 12 }}>{errors.bic}</div>}
                </Form.Group>
              </div>

              {profile && profile.requiredAgreementFields.map((f) => (
                <Form.Group key={f.field}>
                  <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>{f.label}</Form.Label>
                  <Form.Control value={form[f.field] || ''} onChange={(e) => set(f.field, e.target.value)} isInvalid={Boolean(errors[f.field])} />
                  {errors[f.field] && <div className="text-danger" style={{ fontSize: 12 }}>{errors[f.field]}</div>}
                  <div className="text-muted" style={{ fontSize: 12 }}>{f.help}</div>
                </Form.Group>
              ))}

              {profile && (
                <div style={{ fontSize: 12, color: '#475569', background: '#f8fafc', borderRadius: 8, padding: '8px 10px' }}>
                  <strong>Uppladdning:</strong> {profile.uploadChannel}
                </div>
              )}

              <div>
                <button type="button" className="ll-btn ll-btn--primary ll-btn--sm" onClick={save} disabled={saving}>
                  {saving ? 'Sparar…' : 'Spara betalfilsinställningar'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentFileSettings;
